import { Button } from 'antd';
import { BaseButtonProps, NativeButtonProps } from 'antd/lib/button/button';
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import { Color } from '../../config/Color';
import { appOverride } from '../../util/AppUtils';
import { BodySM } from './Typography';

type NextButtonProps = BaseButtonProps &
  NativeButtonProps & {
    hideArrow?: boolean;
  };

export const NextButton: FunctionComponent<NextButtonProps> = (props) => {
  const { hideArrow, children, ...baseProps } = props;
  return (
    <OriginalButton
      data-test="nextButton"
      type="primary"
      size="large"
      htmlType="submit"
      {...baseProps}
    >
      <VerticalAlign>
        <NextButtonContents hideArrow={hideArrow}>
          <BodySM style={{ margin: 0 }}>{children}</BodySM>
          {hideArrow || <ButtonArrow />}
        </NextButtonContents>
      </VerticalAlign>
    </OriginalButton>
  );
};

type SecondaryButtonProps = {
  onClick?: React.MouseEventHandler<HTMLElement>;
  /** Hook for cypress / tests, e.g. `data-test="coverage-address-not-found"`. */
  'data-test'?: string;
};
export const SecondaryButton: FunctionComponent<SecondaryButtonProps> = (props) => {
  return (
    <BaseSecondaryButton onClick={props.onClick} data-test={props['data-test']}>
      <BodySM style={{ color: Color.content.default, margin: 0 }}>{props.children}</BodySM>
    </BaseSecondaryButton>
  );
};

type NextButtonContentsProps = Pick<NextButtonProps, 'hideArrow'>;
const NextButtonContents = styled.div<NextButtonContentsProps>`
  display: flex;
  justify-content: ${({ hideArrow }) => (hideArrow ? 'center' : 'space-between')};
  width: 100%;
  & > p {
    line-height: 49px;
    color: ${appOverride({
      nerva: Color.action.default,
      default: Color.subscription.accent,
    })} !important;
  }

  /* Ensure disabled button text inherits the grey color from parent button */
  button:disabled &,
  button[disabled] & {
    & > p {
      color: inherit !important;
    }
  }
`;

const ButtonArrow = styled.span`
  font-family: ${({ theme }) => theme.font.icon};
  &:before {
    content: '\\e901';
  }
  font-size: ${({ theme }) => theme.fontSize.md};
  display: flex;
  justify-content: center;
  flex-direction: column;
  font-size: ${({ theme }) => theme.fontSize.xs};
  margin-left: ${({ theme }) => theme.spacing._16px};
  color: ${appOverride({ nerva: Color.action.default, default: Color.subscription.accent })};

  /* Ensure disabled button arrow inherits the grey color from parent button */
  button:disabled &,
  button[disabled] & {
    color: inherit !important;
  }
`;

const VerticalAlign = styled.div`
  align-items: stretch;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
`;

const OriginalButton = styled(Button)`
  height: 50px;
  min-width: ${appOverride({ nerva: '100%', default: '164px' })};
  width: ${appOverride({ nerva: '100%', default: 'auto' })};
  border-radius: ${appOverride({ nerva: '14px', default: '25px' })};
  padding: ${({ theme }) => `0 ${theme.spacing._26px}`};
  ${({ theme }) => theme.shadow.drop.gray}
  margin: auto;
  & {
    background: ${Color.flow.accent};
    border-color: ${Color.flow.accent};
    color: ${appOverride({ nerva: Color.action.default, default: Color.subscription.accent })};
  }
  &:hover,
  &:focus {
    background: ${Color.flow.onAccent};
    border-color: ${Color.flow.accent};
    color: ${appOverride({ nerva: Color.action.default, default: Color.subscription.accent })};
  }
  &:focus {
    ${({ theme }) => theme.shadow.drop.dark}
  }
  &:disabled,
  &[disabled] {
    background: ${Color.flow.disabled} !important;
    border-color: ${appOverride({ nerva: 'transparent', default: Color.flow.disabled })} !important;
    color: ${appOverride({ nerva: '#A0A0A0', default: '#FFFFFF' })} !important;
    cursor: not-allowed;
    opacity: ${appOverride({ nerva: '1', default: '0.6' })};
  }
  .ant-btn-loading-icon {
    line-height: 49px;
    .anticon {
      background: ${Color.action.default};
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  }
  display: flex;
`;

const BaseSecondaryButton = styled(Button)`
  height: ${({ theme }) => theme.spacing._40px};
  padding: ${({ theme }) => theme.spacing._6px};
  border: 0;
  align-content: center;
  box-shadow: 0 0;
  margin-bottom: 0;
  background: transparent !important;
  color: ${Color.content.default} !important;

  &:hover {
    background: transparent !important;
    color: ${Color.content.default} !important;
  }
`;
